import {
  Button as MuiButton,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Autocomplete as MuiAutocomplete,
  Paper,
  Tooltip,
  styled,
} from "@mui/material";
import { useContext, useState } from "react";
import { Button, TextField, Typography } from "@platform/design-system";
import { RFP_ACTION_VALUES } from "@platform/rfp-core";

import { submitEvent } from "../../../utils/tracking";
import AutocompleteTextField from "../../../components/autocompleteTextField";
import ConfirmDialog from "../../../components/confirmDialog";
import SelectedCarriers from "../../../components/selectedCarriers";
import useTierCarriers from "../../../hooks/useTierCarriers";
import {
  CarrierSelectionOptions,
  CoveragesNav,
  SectionTitle,
  StackAddCarrier,
} from "../components";
import { RFP_ACTION_TYPES } from "../constants";
import { RfpContext } from "../contexts";

const {
  CARRIERS,
  SELECTED_COVERAGES,
  STRATEGIC_CARRIERS,
  SELECTED_CARRIERS,
  IS_CENTRO_CARRIER_SELECTED,
  CLIENT_DETAIL,
} = RFP_ACTION_VALUES;
const { UPDATE_SELECTED_CARRIERS, UPDATE_CENTRO_SELECTED_CARRIER } =
  RFP_ACTION_TYPES;

export const Autocomplete = styled(MuiAutocomplete)(({ theme }) => ({
  ".MuiInputLabel-root:not(.Mui-focused)": {
    color: theme.palette.text.secondary,
  },
  ".MuiOutlinedInput-root.MuiInputBase-root": {
    paddingRight: 16,
  },
}));

export const CarrierButton = styled(MuiButton)(() => ({
  fontSize: 16,
  textAlign: "left",
  width: "100%",
  justifyContent: "flex-start",
}));

const CarrierSelection = ({ px }) => {
  const { rfp, setRfp } = useContext(RfpContext);
  const carriers = rfp[CARRIERS];
  const strategicCarriers = rfp[STRATEGIC_CARRIERS] ?? [];
  const selectedCarriers = rfp[SELECTED_CARRIERS];
  const centroCarrierSelected = rfp[IS_CENTRO_CARRIER_SELECTED];
  const centroCarrierEnabled =
    rfp[CLIENT_DETAIL]?.centro_select_carrier_enabled;

  const [enableAddCarrier, setEnableAddCarrier] = useState(false);
  const [newCarrier, setNewCarrier] = useState("");
  const [changeToCentroDialogOpen, setChangeToCentroDialogOpen] =
    useState(false);
  const [isOwnCarrierSelected, setIsOwnCarrierSelected] = useState(
    selectedCarriers.length ? true : false,
  );

  const tierCarriers = useTierCarriers(strategicCarriers);

  const handleKeyDown = ({ key, target }) => {
    if (key === "Enter" && target.value) {
      handleAddCarrier();
    }
  };

  function handleSelectCarrier(carrier) {
    submitEvent("Carrier Added", {
      carrier_name: carrier.name,
      carrier_status: "strategic",
    });
    setRfp({ type: UPDATE_SELECTED_CARRIERS, value: { name: carrier } });
  }

  const handleAddCarrier = () => {
    const strategicCarrier = strategicCarriers?.find(
      (sc) => sc.name.toLowerCase() === newCarrier.trim().toLocaleLowerCase(),
    );
    if (strategicCarrier) {
      submitEvent("Custom Carrier Added", {
        carrier_name: newCarrier,
        carrier_status: "strategic",
        rfp_id: rfp.id,
      });
      handleSelectCarrier(strategicCarrier.name);
      return;
    }

    submitEvent("Custom Carrier Added", {
      carrier_name: newCarrier,
      carrier_status: "non-strategic",
      rfp_id: rfp.id,
    });

    handleSelectCarrier(newCarrier);
    setEnableAddCarrier(false);
    setNewCarrier("");
  };

  const updateCentroSelectCarrier = (value) => {
    if (value) {
      setIsOwnCarrierSelected(false);
    }
    setRfp({
      type: UPDATE_CENTRO_SELECTED_CARRIER,
      value: { selectCentro: value },
    });
  };

  const handleCarrierChange = (value) => {
    if (value === "centro") {
      if (selectedCarriers.length) {
        setChangeToCentroDialogOpen(true);
      } else {
        updateCentroSelectCarrier(true);
      }
    } else if (value === "own" && !isOwnCarrierSelected) {
      setIsOwnCarrierSelected(true);
      updateCentroSelectCarrier(false);
    }
  };

  const handleCentroDialogClose = () => {
    setChangeToCentroDialogOpen(false);
  };

  return (
    <Grid container justifyContent="space-between" px={px} size={12}>
      <Grid size={12}>
        <Typography variant="display-medium" mb={5} textAlign="center">
          Select Carriers to Quote
        </Typography>
      </Grid>
      <Grid component={CoveragesNav} size={3.3}>
        <Typography variant="header-large">Coverages to Quote</Typography>
        <List component="nav" aria-label="selected coverages">
          {rfp[SELECTED_COVERAGES]?.map(({ name }) => (
            <ListItemButton key={name}>
              <ListItemText primary={name} />
            </ListItemButton>
          ))}
        </List>
      </Grid>
      <Grid component={Paper} size={8.4} paddingX={3} pb={8} pt={3}>
        {centroCarrierEnabled && (
          <Grid size={12} mb={2}>
            <CarrierSelectionOptions
              handleCarrierChange={handleCarrierChange}
              centroCarrierSelected={centroCarrierSelected}
              selectedCarriers={selectedCarriers}
              isOwnCarrierSelected={isOwnCarrierSelected}
            />
          </Grid>
        )}
        {!centroCarrierSelected &&
          (isOwnCarrierSelected || !centroCarrierEnabled) && (
            <>
              <Grid size={12} mb={3}>
                <Typography variant="header-large">
                  Selected Carriers
                </Typography>
                <SelectedCarriers
                  containerSx={{
                    border: "1px",
                    borderStyle: "dotted",
                    borderColor: "neutral.500",
                    borderRadius: "5px",
                    padding: 3,
                    marginTop: 3,
                  }}
                  carriers={selectedCarriers}
                  onDelete={(carrier) =>
                    setRfp({
                      type: UPDATE_SELECTED_CARRIERS,
                      value: { name: carrier, delete: true },
                    })
                  }
                />
              </Grid>
              {tierCarriers.map(({ title, tiers }) => (
                <Grid key={title} size={12} mb={2}>
                  <SectionTitle>{title}</SectionTitle>
                  <Grid container display="flex">
                    {tiers.map((carrier) => (
                      <Grid key={carrier.id} size={4} p={1}>
                        <Tooltip title={carrier.name}>
                          <CarrierButton
                            variant="link"
                            disabled={selectedCarriers.includes(carrier.name)}
                            onClick={() => handleSelectCarrier(carrier.name)}
                          >
                            {carrier.name}
                          </CarrierButton>
                        </Tooltip>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ))}
              <Divider />
              <Grid size={12} mt={4} rowGap={2}>
                <AutocompleteTextField
                  fullWidth
                  placeholder="Search Carriers"
                  value={null}
                  onChange={(e, carrier, reason) => {
                    submitEvent("Carrier Added", {
                      carrier_name: carrier.name,
                      carrier_status: "non-strategic",
                    });
                    if (reason === "selectOption") {
                      setRfp({
                        type: UPDATE_SELECTED_CARRIERS,
                        value: { name: carrier.name },
                      });
                    }
                  }}
                  options={carriers}
                  getOptionLabel={({ name }) => name}
                  filterOptions={(options, { inputValue }) =>
                    options.filter(({ name }) => {
                      const nameLC = name.toLowerCase();
                      const inputValueLC = inputValue.toLowerCase();
                      return nameLC.startsWith(inputValueLC);
                    })
                  }
                />
              </Grid>
              <Grid size={12} mt={4} rowGap={2}>
                {enableAddCarrier ? (
                  <StackAddCarrier direction="row">
                    <TextField
                      label="Enter carrier name"
                      required
                      onKeyDown={handleKeyDown}
                      onChange={({ target }) => setNewCarrier(target.value)}
                      sx={{ flex: 3 }}
                      autoComplete="off"
                    />
                    <Button
                      sx={{ flex: 1, margin: "auto 0" }}
                      onClick={handleAddCarrier}
                      variant="primary"
                      disabled={!newCarrier.trim()}
                    >
                      Add Carrier
                    </Button>
                  </StackAddCarrier>
                ) : (
                  <Button
                    onClick={() => setEnableAddCarrier(true)}
                    variant="secondary"
                  >
                    I do not see my carrier
                  </Button>
                )}
              </Grid>
            </>
          )}
      </Grid>
      {changeToCentroDialogOpen ? (
        <ConfirmDialog
          isOpen={changeToCentroDialogOpen}
          title={`Selected Carriers Will Be Removed`}
          message={`You are leaving the “Select my own Carriers” option and any selected carriers will be removed.`}
          subMessage={`Press continue to have Centro select carriers for this RFP.`}
          confirmBtnMsg="Yes, Continue"
          closeBtnMsg="Cancel"
          onClose={handleCentroDialogClose}
          onConfirm={() => {
            updateCentroSelectCarrier(true);
            handleCentroDialogClose();
          }}
        />
      ) : null}
    </Grid>
  );
};

export default CarrierSelection;
